import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { useMeetingDockStore } from "../store/meetingDockStore";
import { useMediaDevices } from "../hooks/useMediaDevices";
import { useMeetingRoom } from "../hooks/useMeetingRoom";
import { createUserTransport } from "../hooks/roomTransport";

type RoomHandle = ReturnType<typeof useMeetingRoom>;
type DevicesHandle = ReturnType<typeof useMediaDevices>;

interface MeetingRoomContextValue {
  occurrenceId: string;
  room: RoomHandle;
  devices: DevicesHandle;
}

const MeetingRoomContext = createContext<MeetingRoomContextValue | null>(null);

/** The live meeting connection, if one is open. Null when no meeting is active. */
export function useActiveMeeting(): MeetingRoomContextValue | null {
  return useContext(MeetingRoomContext);
}

/**
 * Holds the ONE live meeting the logged-in user has open. Mounted globally in
 * ProtectedLayout so the WebRTC connection is created once and survives route
 * changes — the room can be shown full-screen (on the room route) or collapsed
 * into a right-hand dock while the user browses elsewhere, without ever
 * reconnecting. Guests do not use this (their page lives outside the layout).
 */
export function MeetingRoomProvider({ children }: { children: ReactNode }) {
  const occurrenceId = useMeetingDockStore((s) => s.occurrenceId);
  if (!occurrenceId) {
    return (
      <MeetingRoomContext.Provider value={null}>
        {children}
      </MeetingRoomContext.Provider>
    );
  }
  // Keyed on occurrenceId: switching meetings remounts the connection cleanly.
  return (
    <ActiveMeetingProvider key={occurrenceId} occurrenceId={occurrenceId}>
      {children}
    </ActiveMeetingProvider>
  );
}

function ActiveMeetingProvider({
  occurrenceId,
  children,
}: {
  occurrenceId: string;
  children: ReactNode;
}) {
  const devices = useMediaDevices();
  const transport = useMemo(
    () => createUserTransport(occurrenceId),
    [occurrenceId],
  );
  const room = useMeetingRoom({
    occurrenceId,
    transport,
    devicePrefs: devices.prefs,
  });

  const joinedRef = useRef(false);
  // Auto-join the moment the connection is mounted (the pre-join device check
  // lives on the occurrence page — pressing "Dołącz" is the consent gesture).
  useEffect(() => {
    if (joinedRef.current) return;
    joinedRef.current = true;
    void room.join().then(() => void devices.refresh());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Device labels are only exposed by the browser once a capture is granted —
  // refresh the pickers the moment local media becomes available.
  useEffect(() => {
    if (room.localCameraStream) void devices.refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room.localCameraStream]);

  // A fresh object each render is intentional: this provider only re-renders
  // when the room hook's own state changes, so consumers (full view or dock)
  // update with fresh room state exactly as they would if they owned the hook.
  const value: MeetingRoomContextValue = { occurrenceId, room, devices };

  return (
    <MeetingRoomContext.Provider value={value}>
      {children}
    </MeetingRoomContext.Provider>
  );
}
